import { Buffer } from 'buffer'
import ObjectID from 'bson-objectid'
import { isEmpty } from './is_empty'
import { isObjectOnlyType, MAX_RECURSION_DEPTH_MEDIUM } from './common_utils'
import { safeJsonStringify } from './safe_json_parse'

export type ObjectIDType<O = undefined> = string | ObjectID | O

export const ObjectIDHelper = {
  isEqual(valueA: unknown, valueB: unknown): boolean {
    if (typeof valueA === `string` && typeof valueB === `string`)
      return valueA === valueB

    const valueAString = ObjectIDHelper.toString(valueA)
    const valueBString = ObjectIDHelper.toString(valueB)

    return valueAString === valueBString
  },

  isObjectID: (value: unknown): value is ObjectID =>
    typeof value === `object` && !isEmpty(value) && (`toHexString` in value && `id` in value),

  isValidObjectID(value: unknown): value is ObjectID | string {
    try {
      if (ObjectID.isValid(String(value)))
        value = ObjectID(String(value))

      return Boolean(ObjectIDHelper.isObjectID(value))
    } catch {
      return false
    }
  },

  toObjectIDStrict<O>(value: ObjectIDType<O> | undefined): ObjectID {
    if (typeof value === `string`)
      return ObjectID(value)

    if (typeof value === `object` && value && `toHexString` in value && typeof value.toHexString === `function`)
      return ObjectID(value.toHexString())

    throw new Error(`Cannot convert value to ObjectID: ${ safeJsonStringify(value) }`)
  },

  toObjectID<O>(value: ObjectIDType<O> | undefined): ObjectID | undefined {
    try {
      return ObjectIDHelper.toObjectIDStrict(value)
    } catch {
      return void 0
    }
  },

  isCollectionOfObjectID(document: unknown) {
    if (Boolean(document) && typeof document === `object` && !(document instanceof ObjectID)) {
      if (document === null)
        return false
      const intersection = Object.keys(document)
        .some(key => key.match(/.*_id$/gm))
      return Boolean(intersection)
    }

    if (Array.isArray(document))
      return true

    return false
  },

  toString(value: unknown): string | undefined {
    if (!value)
      return void 0

    if (ObjectIDHelper.isValidObjectID(value))
      return String(value)

    if (!isEmpty(value) && isObjectOnlyType(value) && `$oid` in value)
      return ObjectIDHelper.toString(value[`$oid`])
  },
  toStringStrict(value: unknown): string {
    if (!value)
      throw new Error(`Cannot convert undefined: ${ safeJsonStringify(value) }`)

    if (ObjectIDHelper.isValidObjectID(value))
      return String(value)

    if (!isEmpty(value) && isObjectOnlyType(value) && `$oid` in value)
      return ObjectIDHelper.toStringStrict(value[`$oid`])

    throw new Error(`Cannot convert value: ${ safeJsonStringify(value) }`)
  },

  toStringRecurse<T>(maybe: T, stackLimit = MAX_RECURSION_DEPTH_MEDIUM): T | T[Extract<keyof T, string>] | undefined {
    if (stackLimit < 0)
      return maybe

    for (const key in maybe) {
      if (Object.prototype.hasOwnProperty.call(maybe, key)) {
        const value = maybe[key]
        // Avoid recurse primitive data object
        if (
          value instanceof Date
					|| value instanceof Buffer
					|| value !== Object(value)
        )
          return value

        if (ObjectIDHelper.isCollectionOfObjectID(value)) {
          const stringified = ObjectIDHelper.toStringRecurse(value, stackLimit - 1)
          maybe[key as keyof T] = stringified as T[Extract<keyof T, string>]
        } else {
          maybe[key as keyof T] = ObjectIDHelper.toString(value) as any
        }
      }
    }

    return maybe
  },

  generateObjectID(hexStringOrTime?: string | number | Date): ObjectID {
    if (typeof hexStringOrTime === `string`)
      return ObjectID.createFromHexString(hexStringOrTime)

    if (typeof hexStringOrTime === `number` || hexStringOrTime instanceof Date)
      return ObjectID.createFromTime(new Date(hexStringOrTime).valueOf())

    return ObjectID()
  },
  generateObjectIDString(hexStringOrTime?: string | number | Date): string {
    return ObjectIDHelper.generateObjectID(hexStringOrTime).toHexString()
  },

  replaceOIDtoObjectID(
    object: unknown,
    parentKey?: string,
    parentObject?: unknown,
  ) {
    if (object instanceof Object) {
      for (const key of Object.keys(object)) {
        if (typeof object[key] === `object` && object[key] !== null)
          ObjectIDHelper.replaceOIDtoObjectID(object[key], key, object)
        else if (
          key === `$oid`
        && !!parentKey
        && typeof parentObject === `object`
        && parentObject !== null
        )
          parentObject[parentKey as any] = ObjectIDHelper.toObjectIDStrict(object[key])
      }
    }
  },
}

export const {
  toStringRecurse: ObjectIDtoStringRecurse,
  toString: ObjectIDtoString,
  toStringStrict: ObjectIDtoStringStrict,
  generateObjectID,
  isCollectionOfObjectID,
  isObjectID,
  isValidObjectID,
  toObjectIDStrict,
  toObjectID,
} = ObjectIDHelper
